import { useState, useEffect, useMemo, useCallback } from "react";
import styles from "./MainHeader.module.css";
import Button from "../Button";
import Select from "../Forms/Select";
import { scrollArrowsvg } from "../../assets/svg/svgimages";
import useSmoothScroll from "../../hooks/useSmoothScroll";
import { useNavigate, Link } from "react-router-dom";

import { useSelector, useDispatch } from "react-redux";
import {
  fetchLibraryFilterOptions,
  FILTER_SNAPSHOT_BUCKET_ALL,
} from "../../store/slices/productSlice";
import isReactSnap from "../../utils/isReactSnap";
import { getLensDisplayName } from "../../utils/lens";
import { getFilterSnapshotManufacturers } from "../../utils/libraryFilters";

const MainHeader = () => {
  const dispatch = useDispatch();
  const manufacturerTree = useSelector((state) => (
    getFilterSnapshotManufacturers(
      state.products.filterSnapshots[FILTER_SNAPSHOT_BUCKET_ALL]
    )
  ));
  const [selectedManufacturer, setSelectedManufacturer] = useState("");
  const [selectedCamera, setSelectedCamera] = useState("");
  const [selectedLensSeries, setSelectedLensSeries] = useState("");

  const scrollToSection = useSmoothScroll();
  const navigate = useNavigate();

  const handleScroll = () => {
    scrollToSection("about-section");
  };

  useEffect(() => {
    if (isReactSnap) {
      return;
    }
    const request = dispatch(fetchLibraryFilterOptions({ accessType: FILTER_SNAPSHOT_BUCKET_ALL }));
    return () => {
      request.abort();
    };
  }, [dispatch]);

  const manufacturersOptions = useMemo(
    () =>
      manufacturerTree.map((manufacturer) => ({
        id: manufacturer.name,
        label: manufacturer.name,
      })),
    [manufacturerTree]
  );

  const cameraOptions = useMemo(() => {
    if (!selectedManufacturer) {
      return [];
    }

    const manufacturer = manufacturerTree.find(
      (item) => item.name === selectedManufacturer
    );
    if (!manufacturer) {
      return [];
    }

    return manufacturer.cameras.map(({ camera }) => ({
      id: String(camera.id),
      label: camera.model_name || camera.brand || `Camera ${camera.id}`,
    }));
  }, [manufacturerTree, selectedManufacturer]);

  const lensOptions = useMemo(() => {
    if (!selectedManufacturer || !selectedCamera) {
      return [];
    }

    const manufacturer = manufacturerTree.find(
      (item) => item.name === selectedManufacturer
    );
    if (!manufacturer) {
      return [];
    }

    const cameraEntry = manufacturer.cameras.find(
      ({ camera }) => String(camera.id) === selectedCamera
    );
    if (!cameraEntry) {
      return [];
    }

    const allSeriesGroups = (cameraEntry.lensBrands || []).flatMap(
      (lensBrand) => lensBrand.seriesGroups || []
    );

    return allSeriesGroups.map((lensSeries) => ({
      id: lensSeries.key,
      label:
        lensSeries.label
        || getLensDisplayName(lensSeries.lenses?.[0], { includeFocal: false })
        || `Lens series ${lensSeries.key}`,
    }));
  }, [manufacturerTree, selectedManufacturer, selectedCamera]);

  useEffect(() => {
    setSelectedCamera("");
    setSelectedLensSeries("");
  }, [selectedManufacturer]);

  useEffect(() => {
    setSelectedLensSeries("");
  }, [selectedCamera]);

  const handleSubmit = useCallback(() => {
    const params = new URLSearchParams();

    if (selectedCamera) {
      params.append("camera", selectedCamera);
    } else if (selectedManufacturer) {
      // 8.6: Если выбран только производитель — передаём все его camera IDs
      const manufacturer = manufacturerTree.find(
        (item) => item.name === selectedManufacturer
      );
      if (manufacturer) {
        manufacturer.cameras.forEach(({ camera }) => {
          params.append("camera", String(camera.id));
        });
      }
    }
    if (selectedLensSeries) {
      params.append("lens_series", selectedLensSeries);
    }

    navigate(`/library?${params.toString()}`);
  }, [navigate, manufacturerTree, selectedManufacturer, selectedCamera, selectedLensSeries]);

  return (
    <section className={styles.main}>
      <div className={styles.contentcont}>
        <h1 className={`h1-bold ${styles.title}`}>Distortion grids database</h1>
        <p className="h3-light">
          You can easily search ready-to-use distortion grids from our database.
          Just select the desired camera and lens series, then click the 'Browse'
          button, or see the full library of available assets by clicking the
          link below.
        </p>

        <div className={styles.searchcont}>
          <form className={styles.form}>
            <Select
              placeholder="Manufacturer"
              options={manufacturersOptions}
              selected={selectedManufacturer}
              onSelect={setSelectedManufacturer}
            />
            <Select
              placeholder="Camera"
              options={cameraOptions}
              selected={selectedCamera}
              onSelect={setSelectedCamera}
              disabled={!selectedManufacturer}
              helperText={
                !selectedManufacturer ? "Select a manufacturer first" : undefined
              }
            />
            <Select
              placeholder="Lens Series"
              options={lensOptions}
              selected={selectedLensSeries}
              onSelect={setSelectedLensSeries}
              disabled={!selectedManufacturer || !selectedCamera}
              helperText={
                !selectedManufacturer
                  ? "Select a manufacturer first"
                  : !selectedCamera
                  ? "Select a camera first"
                  : undefined
              }
            />
            <div className={styles.formbtn}>
              <Button
                label="Browse"
                variant="blue"
                iconType="submit"
                onClick={handleSubmit}
              />
            </div>
          </form>
        </div>
        <div className={styles.btn}>
          <Link to="/library">
            <Button
              labelPosition="left"
              variant="blue"
              label="See full library"
              iconType="arrowRight"
            />
          </Link>

          <button className={styles.scroll} onClick={handleScroll}>
            {scrollArrowsvg}
          </button>
        </div>
      </div>
    </section>
  );
};

export default MainHeader;
